function assertFinitePoint(point, label) {
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    throw new TypeError(`${label} must contain finite x and y values`);
  }
}

function assertViewport(viewport) {
  if (!viewport || !Number.isFinite(viewport.scale) || viewport.scale <= 0) {
    throw new TypeError('viewport scale must be a positive finite number');
  }
  assertFinitePoint(viewport.offset, 'viewport offset');
}

export function screenToWorld(screenPoint, containerRect, viewport) {
  assertFinitePoint(screenPoint, 'screen point');
  assertViewport(viewport);

  return viewportToWorld(clientToViewport(screenPoint, containerRect), viewport);
}

export function clientToViewport(clientPoint, containerRect) {
  assertFinitePoint(clientPoint, 'client point');

  const left = Number.isFinite(containerRect?.left) ? containerRect.left : 0;
  const top = Number.isFinite(containerRect?.top) ? containerRect.top : 0;

  return {
    x: clientPoint.x - left,
    y: clientPoint.y - top,
  };
}

export function viewportToWorld(viewportPoint, viewport) {
  assertFinitePoint(viewportPoint, 'viewport point');
  assertViewport(viewport);
  return {
    x: (viewportPoint.x - viewport.offset.x) / viewport.scale,
    y: (viewportPoint.y - viewport.offset.y) / viewport.scale,
  };
}

export function worldToViewport(worldPoint, viewport) {
  assertFinitePoint(worldPoint, 'world point');
  assertViewport(viewport);
  return {
    x: viewport.offset.x + worldPoint.x * viewport.scale,
    y: viewport.offset.y + worldPoint.y * viewport.scale,
  };
}

export function worldToScreen(worldPoint, containerRect, viewport) {
  assertFinitePoint(worldPoint, 'world point');
  assertViewport(viewport);

  const left = Number.isFinite(containerRect?.left) ? containerRect.left : 0;
  const top = Number.isFinite(containerRect?.top) ? containerRect.top : 0;
  const viewportPoint = worldToViewport(worldPoint, viewport);

  return {
    x: left + viewportPoint.x,
    y: top + viewportPoint.y,
  };
}

export function clampViewportOverlay(point, viewportSize, overlaySize, padding = 8) {
  const safePadding = Math.max(0, Number.isFinite(padding) ? padding : 8);
  const viewportWidth = Math.max(0, Number.isFinite(viewportSize?.width) ? viewportSize.width : 0);
  const viewportHeight = Math.max(0, Number.isFinite(viewportSize?.height) ? viewportSize.height : 0);
  const overlayWidth = Math.max(0, Number.isFinite(overlaySize?.width) ? overlaySize.width : 0);
  const overlayHeight = Math.max(0, Number.isFinite(overlaySize?.height) ? overlaySize.height : 0);
  const minX = safePadding;
  const minY = safePadding;
  const maxX = Math.max(minX, viewportWidth - overlayWidth - safePadding);
  const maxY = Math.max(minY, viewportHeight - overlayHeight - safePadding);

  return {
    x: Math.min(Math.max(Number.isFinite(point?.x) ? point.x : minX, minX), maxX),
    y: Math.min(Math.max(Number.isFinite(point?.y) ? point.y : minY, minY), maxY),
  };
}

export function getPointDistance(first, second) {
  assertFinitePoint(first, 'first point');
  assertFinitePoint(second, 'second point');
  return Math.hypot(second.x - first.x, second.y - first.y);
}

export function getPointMidpoint(first, second) {
  assertFinitePoint(first, 'first point');
  assertFinitePoint(second, 'second point');
  return {
    x: (first.x + second.x) / 2,
    y: (first.y + second.y) / 2,
  };
}

export function zoomViewportAtPoint(viewport, newScale, anchorPoint) {
  assertViewport(viewport);
  assertFinitePoint(anchorPoint, 'zoom anchor');
  if (!Number.isFinite(newScale) || newScale <= 0) {
    throw new TypeError('new scale must be a positive finite number');
  }
  const worldAnchor = {
    x: (anchorPoint.x - viewport.offset.x) / viewport.scale,
    y: (anchorPoint.y - viewport.offset.y) / viewport.scale,
  };
  return {
    scale: newScale,
    offset: {
      x: anchorPoint.x - worldAnchor.x * newScale,
      y: anchorPoint.y - worldAnchor.y * newScale,
    },
  };
}

export function calculatePinchViewport({
  initialViewport,
  initialPoints,
  currentPoints,
  minScale = 0.2,
  maxScale = 3,
}) {
  assertViewport(initialViewport);
  if (!Array.isArray(initialPoints) || initialPoints.length !== 2) {
    throw new TypeError('initial pinch requires exactly two points');
  }
  if (!Array.isArray(currentPoints) || currentPoints.length !== 2) {
    throw new TypeError('current pinch requires exactly two points');
  }
  const initialDistance = getPointDistance(initialPoints[0], initialPoints[1]);
  if (initialDistance <= 0) return initialViewport;
  const currentDistance = getPointDistance(currentPoints[0], currentPoints[1]);
  const requestedScale = initialViewport.scale * (currentDistance / initialDistance);
  const scale = Math.min(maxScale, Math.max(minScale, requestedScale));
  const initialMidpoint = getPointMidpoint(initialPoints[0], initialPoints[1]);
  const currentMidpoint = getPointMidpoint(currentPoints[0], currentPoints[1]);
  const worldAnchor = {
    x: (initialMidpoint.x - initialViewport.offset.x) / initialViewport.scale,
    y: (initialMidpoint.y - initialViewport.offset.y) / initialViewport.scale,
  };
  return {
    scale,
    offset: {
      x: currentMidpoint.x - worldAnchor.x * scale,
      y: currentMidpoint.y - worldAnchor.y * scale,
    },
  };
}

export function fitBoundsToViewport(bounds, viewportSize, options = {}) {
  const { width, height } = viewportSize || {};
  if (!bounds || ![bounds.minX, bounds.minY, bounds.maxX, bounds.maxY].every(Number.isFinite)) {
    throw new TypeError('bounds must contain finite min/max values');
  }
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    throw new TypeError('viewport size must contain positive width and height');
  }

  const padding = Number.isFinite(options.padding) ? Math.max(0, options.padding) : 80;
  const minScale = Number.isFinite(options.minScale) ? options.minScale : 0.2;
  const maxScale = Number.isFinite(options.maxScale) ? options.maxScale : 1.5;
  const boundsWidth = Math.max(1, bounds.maxX - bounds.minX);
  const boundsHeight = Math.max(1, bounds.maxY - bounds.minY);
  const availableWidth = Math.max(1, width - padding * 2);
  const availableHeight = Math.max(1, height - padding * 2);
  const scale = Math.min(
    maxScale,
    Math.max(minScale, Math.min(availableWidth / boundsWidth, availableHeight / boundsHeight))
  );
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  return {
    scale,
    offset: {
      x: width / 2 - centerX * scale,
      y: height / 2 - centerY * scale,
    },
  };
}
